import { createReducer } from '@reduxjs/toolkit';

import { MessengerTranslatedLanguage } from 'components/settings/components/sections/dixaMessenger/views/settings/components/generalSettings/languageSettings/languages';

import { knowledgeBaseMode } from './constants';
import { messengerSettingsActions as actions } from './messengerSettings.actions';
import { getFirstKbIntegrationTranslatedLanguage } from './messengerSettings.reducer.utils';
import {
  DixaKnowledgeIntegration,
  ElevioConnectedState,
  ElevioIntegrationState,
  MessengerKnowledgeBaseMode,
  MessengerKnowledgeBaseType,
  TranslatedString,
} from './messengerSettings.types';

const makeTranslatedString = (
  translatedMap: Partial<Record<MessengerTranslatedLanguage, string>> = {},
): TranslatedString => ({
  translatedMap,
  maxLength: 50,
});

const mkConnectedState = (
  companyId: string,
  enabled: boolean,
  findArticles: TranslatedString = makeTranslatedString(),
  searchBarPlaceholder: TranslatedString = makeTranslatedString(),
  displayType: MessengerKnowledgeBaseMode = knowledgeBaseMode.displayLinkToArticles,
): ElevioConnectedState => ({
  type: 'connected',
  companyId,
  enabled,
  displayType,
  translations: {
    findArticles,
    searchBarPlaceholder,
  },
});

export type MessengerSettingsReducerState = {
  selectedLanguage: MessengerTranslatedLanguage;
  knowledgeBaseType: MessengerKnowledgeBaseType;
  elevioIntegration: ElevioIntegrationState;
  pristineElevioIntegration: ElevioIntegrationState;
  dixaKnowledgeIntegration: DixaKnowledgeIntegration;
  pristineDixaKnowledgeIntegration: DixaKnowledgeIntegration;
};

const defaultDixaKnowledgeIntegration: DixaKnowledgeIntegration = {
  helpcenterId: undefined,
  enabled: true,
  translations: {
    findArticles: makeTranslatedString(),
    searchBarPlaceholder: makeTranslatedString(),
  },
  displayType: knowledgeBaseMode.displayLinkToArticles,
};

const initialState: MessengerSettingsReducerState = {
  selectedLanguage: 'en',
  // keep elevio as default for backwards compatibility
  knowledgeBaseType: 'elevio',
  elevioIntegration: {
    type: 'not-connected',
  },
  pristineElevioIntegration: {
    type: 'not-connected',
  },
  dixaKnowledgeIntegration: defaultDixaKnowledgeIntegration,
  pristineDixaKnowledgeIntegration: defaultDixaKnowledgeIntegration,
};

export const messengerSettingsReducer = createReducer(
  initialState,
  (builder) => {
    builder
      .addCase(actions.initialize, (state, action) => {
        const settings = action.payload;

        if (!settings?.integrations || !settings.general) {
          return;
        }

        if (settings.integrations.knowledge) {
          const dixaKnowledge: DixaKnowledgeIntegration = {
            helpcenterId: settings.integrations.knowledge.helpcenterId,
            enabled: settings.integrations.knowledge.enabled,
            translations: {
              findArticles: makeTranslatedString(settings.general.findArticles),
              searchBarPlaceholder: makeTranslatedString(
                settings.integrations.knowledge.searchBarPlaceholder,
              ),
            },
            displayType:
              settings.integrations.knowledge.displayType ??
              knowledgeBaseMode.displayLinkToArticles,
          };

          state.dixaKnowledgeIntegration = dixaKnowledge;
          state.pristineDixaKnowledgeIntegration = dixaKnowledge;

          // If the dixa kb integration exists, set the selector value to it so that the UX is dixa-kb-first.
          state.knowledgeBaseType = 'dixa_knowledge';
        }

        if (settings.integrations.elevio) {
          const elevioIntegration: ElevioIntegrationState = mkConnectedState(
            settings.integrations.elevio.companyId,
            settings.integrations.elevio.enabled,
            makeTranslatedString(settings.general.findArticles),
            makeTranslatedString(
              settings.integrations.elevio.searchBarPlaceholder,
            ),
            settings.integrations.elevio.displayType,
          );

          state.pristineElevioIntegration = elevioIntegration;
          state.elevioIntegration = elevioIntegration;
        }

        // Set the selected language to the first language with a translation.
        const firstKbIntegrationTranslatedLanguage =
          getFirstKbIntegrationTranslatedLanguage(settings);

        if (firstKbIntegrationTranslatedLanguage) {
          state.selectedLanguage = firstKbIntegrationTranslatedLanguage;
        }
      })
      .addCase(actions.reset, (state) => {
        return {
          ...initialState,
          pristineElevioIntegration: state.pristineElevioIntegration,
          elevioIntegration: state.pristineElevioIntegration,
          dixaKnowledgeIntegration:
            initialState.pristineDixaKnowledgeIntegration,
        };
      })
      .addCase(actions.setElevioCompanyId, (state, action) => {
        if (state.elevioIntegration.type === 'not-connected') {
          state.elevioIntegration = mkConnectedState(action.payload, true);
        } else {
          state.elevioIntegration.companyId = action.payload;
        }
      })
      .addCase(actions.toggleElevioIntegration, (state) => {
        if (state.elevioIntegration.type === 'connected') {
          state.elevioIntegration.enabled = !state.elevioIntegration.enabled;
        }
      })
      .addCase(actions.toggleDixaKnowledgeIntegration, (state) => {
        state.dixaKnowledgeIntegration.enabled =
          !state.dixaKnowledgeIntegration.enabled;
      })
      .addCase(actions.updateDisplayType, (state, action) => {
        switch (state.knowledgeBaseType) {
          case 'elevio': {
            if (state.elevioIntegration.type !== 'not-connected') {
              state.elevioIntegration.displayType = action.payload;
            }
            break;
          }

          case 'dixa_knowledge': {
            state.dixaKnowledgeIntegration.displayType = action.payload;
          }
        }
      })
      .addCase(actions.changeSelectedLanguage, (state, action) => {
        state.selectedLanguage = action.payload;
      })
      .addCase(actions.updateTranslatedString, (state, action) => {
        const { translatable, language, value } = action.payload;

        switch (state.knowledgeBaseType) {
          case 'elevio':
            if (state.elevioIntegration.type !== 'not-connected') {
              state.elevioIntegration.translations[translatable].translatedMap[
                language
              ] = value;
            }
            break;

          case 'dixa_knowledge':
            state.dixaKnowledgeIntegration.translations[
              translatable
            ].translatedMap[language] = value;
            break;
        }
      })
      .addCase(actions.changeKnowledgeBaseType, (state, action) => {
        state.knowledgeBaseType = action.payload;
      })
      .addCase(actions.setDixaKnowledgeHelpCenterId, (state, action) => {
        state.dixaKnowledgeIntegration.helpcenterId = action.payload;
      });
  },
);
